/**
 * React Query hooks for CV-JD Analysis.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@aaas/shared';
import analysisService from '../services/analysis.service';

// ── Query: all mappings for a job (history matrix) ─────────────────────────

export const useJobMappingsQuery = ({
  jobId,
  workspaceId,
  idToken,
  enabled = true,
}: {
  jobId: string | undefined;
  workspaceId: string | undefined;
  idToken: string | undefined;
  enabled?: boolean;
}) =>
  useQuery({
    queryKey: ['cv-jd-mappings', jobId, workspaceId],
    queryFn: () => analysisService.listJobMappings(workspaceId!, idToken!, jobId!),
    enabled: enabled && !!jobId && !!workspaceId && !!idToken,
    // Poll while any mapping is in analyzing state
    refetchInterval: (query) => {
      const items = query.state.data?.items;
      return items?.some((m) => m.analysis_status === 'analyzing' || m.analysis_status === 'pending')
        ? 5000
        : false;
    },
  });

// ── Query: JDs with their CV mappings, paginated (Kanban history view) ─────

export const useJobMappingsByJDQuery = ({
  jobId,
  workspaceId,
  idToken,
  page = 1,
  pageSize = 5,
  search,
  enabled = true,
}: {
  jobId: string | undefined;
  workspaceId: string | undefined;
  idToken: string | undefined;
  page?: number;
  pageSize?: number;
  search?: string;
  enabled?: boolean;
}) =>
  useQuery({
    queryKey: ['cv-jd-mappings-by-jd', jobId, workspaceId, page, pageSize, search],
    queryFn: () =>
      analysisService.listJobMappingsByJD(workspaceId!, idToken!, jobId!, {
        page,
        page_size: pageSize,
        search: search || undefined,
      }),
    enabled: enabled && !!jobId && !!workspaceId && !!idToken,
    refetchInterval: (query) => {
      const items = query.state.data?.items ?? [];
      const hasActive = items.some((jd) =>
        jd.cv_mappings.some(
          (m) => m.analysis_status === 'analyzing' || m.analysis_status === 'pending',
        ),
      );
      return hasActive ? 5000 : false;
    },
  });

// ── Mutation: delete mapping ────────────────────────────────────────────────

export const useDeleteMappingMutation = (jobId: string) => {
  const queryClient = useQueryClient();
  const { user, currentWorkspace, idToken } = useAuth();

  return useMutation({
    mutationFn: async (mappingId: string) => {
      const token = idToken || (await user!.getIdToken());
      return analysisService.deleteMapping(currentWorkspace!.id, token, jobId, mappingId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cv-jd-mappings-by-jd', jobId] });
    },
  });
};

// ── Mutation: trigger analysis ──────────────────────────────────────────────

export const useTriggerAnalysisMutation = (jobId: string) => {
  const queryClient = useQueryClient();
  const { user, currentWorkspace, idToken } = useAuth();

  return useMutation({
    mutationFn: async ({ cvId, jdId }: { cvId: string; jdId: string }) => {
      const token = idToken || (await user!.getIdToken());
      return analysisService.triggerAnalysis(
        currentWorkspace!.id,
        token,
        cvId,
        jdId,
      );
    },
    onSuccess: () => {
      // Invalidate history so it refetches and shows new pending row
      queryClient.invalidateQueries({ queryKey: ['cv-jd-mappings', jobId] });
    },
  });
};
